import { Injectable } from '@angular/core';
import {
  LoginFlow,
  FrontendApiUpdateLoginFlowRequest,
  UpdateLoginFlowBody,
} from '@ory/client'
import {
  Observable,
} from 'rxjs';
import { OryCreateInput, OryExecuteInput, OryExecuteOutput, OryGetOutput } from '../../model';
import type { OryGetInput } from '../../model';
import { OryKratosBaseFlow } from './base.flow';
import { OryFlowReader } from '../../utils';
import type {
  OryCreatableFlow,
  OryRetrievableFlow,
  OryExecutableFlow,
} from '../flow-capabilities';

/**
 * Implements Ory Kratos login flow for user authentication.
 *
 * Supports multiple authentication methods:
 * - Password authentication
 * - Social sign-in (OIDC providers)
 * - Passwordless (WebAuthn, magic links)
 *
 * Successful login establishes an authenticated session.
 */
@Injectable({
  providedIn: 'root',
})
export class OryLoginFlow<IdentityTraits extends object = object>
  extends OryKratosBaseFlow
  implements
    OryCreatableFlow<'login', IdentityTraits>,
    OryRetrievableFlow<'login', IdentityTraits>,
    OryExecutableFlow<'login', IdentityTraits>
{
  create(
    input: OryCreateInput<'login'> = {},
  ): Observable<LoginFlow> {
    return this.fromOryRequest(this.iamApi.createBrowserLoginFlow(input));
  }

  get(input: OryGetInput<'login'>): Observable<LoginFlow> {
    return this.fromOryRequest(this.iamApi.getLoginFlow(input));
  }

  execute(
    flow: OryGetOutput<'login', IdentityTraits>,
    input: OryExecuteInput<'login'>,
  ): Observable<OryExecuteOutput<'login', IdentityTraits>> {
    const csrfToken = OryFlowReader.csrfToken(flow);
    const updateLoginFlowBody: UpdateLoginFlowBody =
      input.method === 'deviceauthn'
        ? input
        : { ...input, csrf_token: csrfToken };
    const apiRequest: FrontendApiUpdateLoginFlowRequest = {
      flow: flow.id,
      updateLoginFlowBody,
    };
    return this.fromOryRequest(this.iamApi.updateLoginFlow(apiRequest));
  }

}
